import { useEffect, useState } from 'react'
import { accountAPI, keywordAPI, contentSettingsAPI, adminCalendarAPI, adminGenerationRuleAPI } from '../../services/api'
import ContentCalendarPage from '../user/ContentCalendarPage'

const CATEGORIES = [
  { value: '뷰티/패션', icon: 'checkroom' },
  { value: '식음료/F&B', icon: 'restaurant' },
  { value: '여행/관광', icon: 'flight' },
  { value: '테크/IT', icon: 'devices' },
  { value: '건강/피트니스', icon: 'fitness_center' },
  { value: '교육/자기계발', icon: 'school' },
  { value: '엔터테인먼트', icon: 'movie' },
  { value: '라이프스타일', icon: 'spa' },
  { value: '기타', icon: 'more_horiz' },
]

const TONE_OPTIONS = [
  { value: 'FRIENDLY_EMOJI', label: '친근한 이모지 톤', desc: '이모지와 반말 사용' },
  { value: 'PROFESSIONAL', label: '전문적인 톤', desc: '존댓말과 정보 중심' },
  { value: 'HUMOROUS_TREND', label: '유머/트렌디', desc: '밈과 유행어 활용' },
]

const MOOD_OPTIONS = ['Minimal', 'Colorful', 'Vintage', 'High-res Realistic', 'Illustration']

const CAPTION_LENGTH_OPTIONS = [
  { value: 'SHORT', label: '짧게', desc: '1-2문장, 200자 이내' },
  { value: 'MEDIUM', label: '보통', desc: '2-4문장, 500자 이내' },
  { value: 'LONG', label: '길게', desc: '4-6문장, 800자 이내' },
]

const CTA_OPTIONS = [
  { value: 'NONE', label: '없음', icon: 'block' },
  { value: 'LIKE', label: '좋아요', icon: 'favorite' },
  { value: 'FOLLOW', label: '팔로우', icon: 'person_add' },
  { value: 'COMMENT', label: '댓글', icon: 'chat_bubble' },
  { value: 'SAVE', label: '저장', icon: 'bookmark' },
  { value: 'LINK', label: '링크', icon: 'link' },
]

const EMOJI_DENSITY_OPTIONS = [
  { value: 'NONE', label: '없음', desc: '이모지 사용 안 함' },
  { value: 'LOW', label: '적음', desc: '1-2개만 사용' },
  { value: 'MEDIUM', label: '보통', desc: '적절하게 사용' },
  { value: 'HIGH', label: '많음', desc: '풍성하게 사용' },
]

const IMAGE_STYLE_OPTIONS = [
  { value: 'PHOTO', label: '사진', icon: 'photo_camera' },
  { value: 'FLAT_LAY', label: '플랫레이', icon: 'grid_view' },
  { value: 'CLOSE_UP', label: '클로즈업', icon: 'zoom_in' },
  { value: 'EDITORIAL', label: '에디토리얼', icon: 'auto_stories' },
  { value: 'LIFESTYLE', label: '라이프', icon: 'coffee' },
  { value: 'MINIMAL_GRAPHIC', label: '미니멀', icon: 'category' },
]

function MemberManagementPage() {
  const [accounts, setAccounts] = useState([])
  const [schedules, setSchedules] = useState({})
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [success, setSuccess] = useState(null)

  // Modal state
  const [modalAccount, setModalAccount] = useState(null)
  const [modalSchedule, setModalSchedule] = useState(null)
  const [modalKeywords, setModalKeywords] = useState([])
  const [modalTab, setModalTab] = useState('account')
  const [newTime, setNewTime] = useState('09:00')
  const [modalLoading, setModalLoading] = useState(false)

  // Content settings state
  const [contentSettings, setContentSettings] = useState(null)
  const [contentDirty, setContentDirty] = useState(false)
  const [hashtagInput, setHashtagInput] = useState('')

  // Generation rule state
  const [generationRule, setGenerationRule] = useState(null)
  const [generationRuleLoading, setGenerationRuleLoading] = useState(false)

  useEffect(() => {
    loadAll()
  }, [])

  const loadAll = async () => {
    try {
      setLoading(true)
      setError(null)
      const accountRes = await accountAPI.getAll()
      const accts = accountRes.data || []
      setAccounts(accts)

      const scheduleMap = {}
      await Promise.all(
        accts.map(async (acct) => {
          try {
            const res = await accountAPI.getSchedule(acct.id)
            scheduleMap[acct.id] = res.data
          } catch {
            scheduleMap[acct.id] = null
          }
        })
      )
      setSchedules(scheduleMap)
    } catch {
      setError('데이터를 불러오는데 실패했습니다.')
    } finally {
      setLoading(false)
    }
  }

  const showSuccess = (msg) => {
    setSuccess(msg)
    setTimeout(() => setSuccess(null), 3000)
  }

  // Quick toggle from card
  const handleQuickToggle = async (accountId) => {
    const sch = schedules[accountId]
    if (!sch) return
    try {
      const response = await accountAPI.updateSchedule(accountId, {
        autoUploadEnabled: !sch.autoUploadEnabled,
        uploadTimes: sch.uploadTimes,
        dailyPostLimit: sch.dailyPostLimit
      })
      setSchedules(prev => ({ ...prev, [accountId]: response.data }))
      showSuccess(`자동 업로드가 ${response.data.autoUploadEnabled ? '활성화' : '비활성화'}되었습니다.`)
    } catch {
      setError('설정 변경에 실패했습니다.')
    }
  }

  // Open modal
  const openModal = async (account) => {
    setModalAccount(account)
    setModalSchedule(schedules[account.id])
    setModalTab('account')
    setNewTime('09:00')
    setContentSettings(null)
    setContentDirty(false)
    setHashtagInput('')
    setGenerationRule(null)
    // Load keywords
    try {
      const res = await keywordAPI.getByAccountId(account.id)
      setModalKeywords(res.data || [])
    } catch {
      setModalKeywords([])
    }
    // Load content settings
    try {
      const res = await contentSettingsAPI.get(account.id)
      setContentSettings(res.data || {})
    } catch {
      setContentSettings({})
    }
    // Load generation rule
    try {
      const res = await adminGenerationRuleAPI.get(account.id)
      setGenerationRule(res.data || null)
    } catch {
      setGenerationRule(null)
    }
  }

  const closeModal = () => {
    setModalAccount(null)
    setModalSchedule(null)
    setModalKeywords([])
    setContentSettings(null)
    setContentDirty(false)
    setGenerationRule(null)
  }

  // === Schedule actions ===
  const handleToggleAutoUpload = async () => {
    if (!modalAccount || !modalSchedule) return
    try {
      setModalLoading(true)
      const response = await accountAPI.updateSchedule(modalAccount.id, {
        autoUploadEnabled: !modalSchedule.autoUploadEnabled,
        uploadTimes: modalSchedule.uploadTimes,
        daysOfWeek: modalSchedule.daysOfWeek,
        dailyPostLimit: modalSchedule.dailyPostLimit
      })
      setModalSchedule(response.data)
      setSchedules(prev => ({ ...prev, [modalAccount.id]: response.data }))
      showSuccess('자동 업로드 설정이 변경되었습니다.')
    } catch {
      setError('설정 변경에 실패했습니다.')
    } finally {
      setModalLoading(false)
    }
  }

  const handleAddTime = async () => {
    if (!modalAccount) return
    try {
      setModalLoading(true)
      const response = await accountAPI.addUploadTime(modalAccount.id, newTime)
      setModalSchedule(response.data)
      setSchedules(prev => ({ ...prev, [modalAccount.id]: response.data }))
      showSuccess(`업로드 시간 ${newTime}이 추가되었습니다.`)
      setNewTime('09:00')
    } catch {
      setError('시간 추가에 실패했습니다.')
    } finally {
      setModalLoading(false)
    }
  }

  const handleRemoveTime = async (time) => {
    if (!modalAccount) return
    try {
      setModalLoading(true)
      const response = await accountAPI.removeUploadTime(modalAccount.id, time)
      setModalSchedule(response.data)
      setSchedules(prev => ({ ...prev, [modalAccount.id]: response.data }))
      showSuccess(`업로드 시간 ${time}이 제거되었습니다.`)
    } catch {
      setError('시간 제거에 실패했습니다.')
    } finally {
      setModalLoading(false)
    }
  }

  const handleToggleDay = async (dayNum) => {
    if (!modalAccount || !modalSchedule) return
    const currentDays = modalSchedule.daysOfWeek || []
    const newDays = currentDays.includes(dayNum)
      ? currentDays.filter(d => d !== dayNum)
      : [...currentDays, dayNum].sort()
    try {
      setModalLoading(true)
      const response = await accountAPI.updateSchedule(modalAccount.id, {
        autoUploadEnabled: modalSchedule.autoUploadEnabled,
        uploadTimes: modalSchedule.uploadTimes,
        daysOfWeek: newDays,
        dailyPostLimit: modalSchedule.dailyPostLimit
      })
      setModalSchedule(response.data)
      setSchedules(prev => ({ ...prev, [modalAccount.id]: response.data }))
    } catch {
      setError('요일 변경에 실패했습니다.')
    } finally {
      setModalLoading(false)
    }
  }

  const handleUpdateLimit = async (newLimit) => {
    if (!modalAccount || !modalSchedule) return
    const parsed = parseInt(newLimit)
    if (isNaN(parsed) || parsed < 1) return
    try {
      setModalLoading(true)
      const response = await accountAPI.updateSchedule(modalAccount.id, {
        autoUploadEnabled: modalSchedule.autoUploadEnabled,
        uploadTimes: modalSchedule.uploadTimes,
        daysOfWeek: modalSchedule.daysOfWeek,
        dailyPostLimit: parsed
      })
      setModalSchedule(response.data)
      setSchedules(prev => ({ ...prev, [modalAccount.id]: response.data }))
      showSuccess('일일 포스트 제한이 변경되었습니다.')
    } catch {
      setError('설정 변경에 실패했습니다.')
    } finally {
      setModalLoading(false)
    }
  }

  // === Keyword actions ===
  const handleToggleKeyword = async (keywordId) => {
    try {
      setModalLoading(true)
      await keywordAPI.toggle(keywordId)
      const res = await keywordAPI.getByAccountId(modalAccount.id)
      setModalKeywords(res.data || [])
      showSuccess('키워드 상태가 변경되었습니다.')
    } catch {
      setError('키워드 변경에 실패했습니다.')
    } finally {
      setModalLoading(false)
    }
  }

  // === Generation Rule actions ===
  const handleResetGenerationRule = async () => {
    if (!modalAccount || !generationRule) return
    try {
      setGenerationRuleLoading(true)
      const res = await adminGenerationRuleAPI.reset(modalAccount.id)
      setGenerationRule(res.data)
      showSuccess('생성 규칙 상태가 초기화되었습니다.')
    } catch {
      setError('상태 초기화에 실패했습니다.')
    } finally {
      setGenerationRuleLoading(false)
    }
  }

  // === Content Settings actions ===
  const updateContentField = (field, value) => {
    setContentSettings(prev => ({ ...prev, [field]: value }))
    setContentDirty(true)
  }

  const getHashtags = () => {
    if (!contentSettings?.permanentHashtags) return []
    return contentSettings.permanentHashtags
      .split(',')
      .map(t => t.trim().replace(/^#/, ''))
      .filter(Boolean)
  }

  const addHashtag = () => {
    const tag = hashtagInput.trim().replace(/^#/, '')
    if (!tag) return
    const current = getHashtags()
    if (current.includes(tag)) { setHashtagInput(''); return }
    const newTags = [...current, tag].map(t => `#${t}`).join(',')
    updateContentField('permanentHashtags', newTags)
    setHashtagInput('')
  }

  const removeHashtag = (tagToRemove) => {
    const newTags = getHashtags().filter(t => t !== tagToRemove).map(t => `#${t}`).join(',')
    updateContentField('permanentHashtags', newTags)
  }

  const handleSaveContentSettings = async () => {
    if (!modalAccount || !contentSettings) return
    try {
      setModalLoading(true)
      await contentSettingsAPI.update(modalAccount.id, contentSettings)
      setContentDirty(false)
      showSuccess('콘텐츠 설정이 저장되었습니다.')
    } catch {
      setError('콘텐츠 설정 저장에 실패했습니다.')
    } finally {
      setModalLoading(false)
    }
  }

  // === Account status actions ===
  const handleUpdateAccountStatus = async (newStatus) => {
    if (!modalAccount) return
    try {
      setModalLoading(true)
      await accountAPI.updateStatus(modalAccount.id, newStatus)
      setModalAccount(prev => ({ ...prev, status: newStatus }))
      setAccounts(prev => prev.map(a => a.id === modalAccount.id ? { ...a, status: newStatus } : a))
      showSuccess(`계정 상태가 ${newStatus}로 변경되었습니다.`)
    } catch {
      setError('상태 변경에 실패했습니다.')
    } finally {
      setModalLoading(false)
    }
  }

  const tabClass = (tab) =>
    `px-3 py-2 text-xs font-semibold rounded-lg transition-colors whitespace-nowrap ${
      modalTab === tab
        ? 'bg-primary text-white'
        : 'text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-[#282839]'
    }`

  const inputClass = 'w-full px-4 h-11 bg-slate-50 dark:bg-[#1c1c27] border border-slate-200 dark:border-[#3b3b54] rounded-xl focus:ring-2 focus:ring-primary focus:border-transparent outline-none text-slate-900 dark:text-white text-sm'

  const statusBadge = (status) => {
    const styles = {
      ACTIVE: 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-200 border-green-200 dark:border-green-700',
      INACTIVE: 'bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400 border-slate-200 dark:border-slate-700',
      TOKEN_INVALID: 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-200 border-red-200 dark:border-red-700',
      SUSPENDED: 'bg-amber-100 dark:bg-amber-900/30 text-amber-800 dark:text-amber-200 border-amber-200 dark:border-amber-700',
    }
    return styles[status] || styles.INACTIVE
  }

  if (loading && accounts.length === 0) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="text-slate-500 dark:text-slate-400 text-lg">로딩 중...</div>
      </div>
    )
  }

  return (
    <div>
      {/* Header */}
      <div className="flex flex-wrap justify-between items-end gap-6 mb-10">
        <div className="flex flex-col gap-2">
          <h2 className="text-4xl font-black tracking-tight text-slate-900 dark:text-white">
            회원 관리
          </h2>
          <p className="text-slate-500 dark:text-slate-400 text-lg">
            회원 계정과 자동화 설정을 관리하세요.
          </p>
        </div>
        <button
          onClick={loadAll}
          disabled={loading}
          className="px-5 py-2.5 bg-slate-100 dark:bg-[#282839] text-slate-700 dark:text-slate-300 rounded-full font-bold text-sm hover:opacity-80 transition-opacity flex items-center gap-2"
        >
          <span className={`material-symbols-outlined text-base ${loading ? 'animate-spin' : ''}`}>refresh</span>
          새로고침
        </button>
      </div>

      {/* Success */}
      {success && (
        <div className="mb-6 p-4 rounded-xl border bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-700 text-green-800 dark:text-green-200 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="material-symbols-outlined">check_circle</span>
            <span>{success}</span>
          </div>
          <button onClick={() => setSuccess(null)} className="material-symbols-outlined hover:opacity-70">close</button>
        </div>
      )}

      {/* Error */}
      {error && (
        <div className="mb-6 p-4 rounded-xl border bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-700 text-red-800 dark:text-red-200 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="material-symbols-outlined">error</span>
            <span>{error}</span>
          </div>
          <button onClick={() => setError(null)} className="material-symbols-outlined hover:opacity-70">close</button>
        </div>
      )}

      {/* Account Cards Grid */}
      {accounts.length === 0 ? (
        <div className="bg-white dark:bg-[#111118] rounded-xl border border-slate-200 dark:border-[#282839] p-20 text-center">
          <span className="material-symbols-outlined text-6xl text-slate-300 dark:text-slate-700 mb-4 block">settings_suggest</span>
          <p className="text-slate-500 dark:text-slate-400 text-lg">등록된 계정이 없습니다.</p>
          <p className="text-slate-400 dark:text-slate-500 text-sm mt-2">계정 설정에서 먼저 계정을 추가하세요.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-5">
          {accounts.map(account => {
            const sch = schedules[account.id]
            const isOn = sch?.autoUploadEnabled
            return (
              <div
                key={account.id}
                className="bg-white dark:bg-[#111118] rounded-xl border border-slate-200 dark:border-[#282839] p-5 hover:shadow-lg transition-shadow"
              >
                {/* Card Header */}
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="size-10 rounded-full bg-gradient-to-tr from-primary to-purple-600 flex items-center justify-center text-white text-sm font-bold">
                      {account.username?.charAt(0)?.toUpperCase()}
                    </div>
                    <div>
                      <h3 className="font-bold text-slate-900 dark:text-white">@{account.username}</h3>
                      <p className="text-xs text-slate-500 dark:text-slate-400">{account.platform}</p>
                    </div>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer" title={isOn ? '자동 업로드 ON' : '자동 업로드 OFF'}>
                    <input
                      type="checkbox"
                      checked={isOn || false}
                      onChange={() => handleQuickToggle(account.id)}
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-slate-200 dark:bg-slate-700 peer-focus:ring-4 peer-focus:ring-primary/30 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary"></div>
                  </label>
                </div>

                {/* Schedule Summary */}
                {sch ? (
                  <div className="space-y-3 mb-4">
                    <div className="flex items-center gap-2">
                      <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium border ${
                        isOn
                          ? 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-200 border-green-200 dark:border-green-700'
                          : 'bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400 border-slate-200 dark:border-slate-700'
                      }`}>
                        <span className="material-symbols-outlined text-sm">{isOn ? 'play_circle' : 'pause_circle'}</span>
                        {isOn ? '자동화 활성' : '자동화 비활성'}
                      </span>
                    </div>

                    <div className="p-3 bg-slate-50 dark:bg-[#1c1c27] rounded-lg">
                      <div className="flex items-center gap-1.5 mb-2">
                        <span className="material-symbols-outlined text-slate-400 dark:text-slate-500 text-sm">schedule</span>
                        <span className="text-xs font-semibold text-slate-600 dark:text-slate-400">업로드 시간</span>
                      </div>
                      {sch.uploadTimes?.length > 0 ? (
                        <div className="flex flex-wrap gap-1.5">
                          {sch.uploadTimes.map(time => (
                            <span key={time} className="px-2 py-0.5 bg-primary/10 text-primary rounded text-xs font-medium">
                              {time}
                            </span>
                          ))}
                        </div>
                      ) : (
                        <span className="text-xs text-slate-400 dark:text-slate-500 italic">설정 없음</span>
                      )}
                    </div>

                    <div className="flex items-center justify-between text-sm">
                      <div className="flex items-center gap-1.5">
                        <span className="material-symbols-outlined text-slate-400 dark:text-slate-500 text-sm">bar_chart</span>
                        <span className="text-slate-600 dark:text-slate-400">일일 제한</span>
                      </div>
                      <span className="font-bold text-slate-900 dark:text-white">
                        {account.todayPostCount || 0} / {sch.dailyPostLimit || 25}
                      </span>
                    </div>
                  </div>
                ) : (
                  <div className="py-4 text-center">
                    <span className="text-sm text-slate-400 dark:text-slate-500 italic">스케줄 정보 없음</span>
                  </div>
                )}

                <button
                  onClick={() => openModal(account)}
                  className="w-full py-2.5 bg-slate-100 dark:bg-[#282839] text-slate-700 dark:text-slate-300 rounded-lg text-sm font-medium hover:opacity-80 transition-opacity flex items-center justify-center gap-2"
                >
                  <span className="material-symbols-outlined text-base">tune</span>
                  상세 설정
                </button>
              </div>
            )
          })}
        </div>
      )}

      {/* ======================== Detail Settings Modal ======================== */}
      {modalAccount && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-[#111118] rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto border border-slate-200 dark:border-[#282839]">
            {/* Modal Header */}
            <div className="sticky top-0 bg-white dark:bg-[#111118] border-b border-slate-200 dark:border-[#282839] p-5 z-10">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="size-10 rounded-full bg-gradient-to-tr from-primary to-purple-600 flex items-center justify-center text-white text-sm font-bold">
                    {modalAccount.username?.charAt(0)?.toUpperCase()}
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-slate-900 dark:text-white">@{modalAccount.username}</h3>
                    <p className="text-xs text-slate-500 dark:text-slate-400">자동화 상세 설정</p>
                  </div>
                </div>
                <button onClick={closeModal} className="material-symbols-outlined text-slate-400 hover:text-slate-600 dark:hover:text-slate-200">
                  close
                </button>
              </div>
              {/* Tabs - 5 tabs matching 4 automation steps + keywords */}
              <div className="flex gap-1.5 overflow-x-auto">
                <button className={tabClass('account')} onClick={() => setModalTab('account')}>
                  <span className="flex items-center gap-1">
                    <span className="material-symbols-outlined text-sm">person</span>
                    계정
                  </span>
                </button>
                <button className={tabClass('content')} onClick={() => setModalTab('content')}>
                  <span className="flex items-center gap-1">
                    <span className="material-symbols-outlined text-sm">edit_note</span>
                    콘텐츠
                  </span>
                </button>
                <button className={tabClass('schedule')} onClick={() => setModalTab('schedule')}>
                  <span className="flex items-center gap-1">
                    <span className="material-symbols-outlined text-sm">schedule</span>
                    스케줄러
                  </span>
                </button>
                <button className={tabClass('keywords')} onClick={() => setModalTab('keywords')}>
                  <span className="flex items-center gap-1">
                    <span className="material-symbols-outlined text-sm">label</span>
                    키워드
                  </span>
                </button>
                <button className={tabClass('generation')} onClick={() => setModalTab('generation')}>
                  <span className="flex items-center gap-1">
                    <span className="material-symbols-outlined text-sm">auto_awesome</span>
                    생성 규칙
                  </span>
                </button>
                <button className={tabClass('calendar')} onClick={() => setModalTab('calendar')}>
                  <span className="flex items-center gap-1">
                    <span className="material-symbols-outlined text-sm">calendar_month</span>
                    캘린더
                  </span>
                </button>
              </div>
            </div>

            {/* Modal Body */}
            <div className="p-5">

              {/* === Account Info Tab (Step 1) === */}
              {modalTab === 'account' && (
                <div className="space-y-5">
                  {/* Status */}
                  <div className="p-4 bg-slate-50 dark:bg-[#1c1c27] rounded-xl">
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="font-bold text-slate-900 dark:text-white">계정 상태</h4>
                      <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border ${statusBadge(modalAccount.status)}`}>
                        <span className="w-1.5 h-1.5 rounded-full bg-current"></span>
                        {modalAccount.status}
                      </span>
                    </div>
                    <div className="flex gap-2">
                      {['ACTIVE', 'INACTIVE', 'SUSPENDED'].map(s => (
                        <button
                          key={s}
                          onClick={() => handleUpdateAccountStatus(s)}
                          disabled={modalLoading || modalAccount.status === s}
                          className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                            modalAccount.status === s
                              ? 'bg-primary text-white'
                              : 'bg-white dark:bg-[#282839] text-slate-600 dark:text-slate-400 border border-slate-200 dark:border-[#3b3b54] hover:bg-slate-100 dark:hover:bg-[#3b3b54]'
                          }`}
                        >
                          {s}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Account Details */}
                  <div className="space-y-3">
                    <h4 className="font-bold text-slate-900 dark:text-white flex items-center gap-2">
                      <span className="material-symbols-outlined text-base">info</span>
                      계정 정보
                    </h4>
                    <div className="grid grid-cols-2 gap-3">
                      <div className="p-3 bg-slate-50 dark:bg-[#1c1c27] rounded-xl">
                        <p className="text-xs text-slate-500 dark:text-slate-400 mb-1">플랫폼</p>
                        <p className="font-medium text-slate-900 dark:text-white text-sm">{modalAccount.platform}</p>
                      </div>
                      <div className="p-3 bg-slate-50 dark:bg-[#1c1c27] rounded-xl">
                        <p className="text-xs text-slate-500 dark:text-slate-400 mb-1">Platform User ID</p>
                        <p className="font-medium text-slate-900 dark:text-white text-sm font-mono">
                          {modalAccount.platformUserId || <span className="text-slate-400 italic">미연결</span>}
                        </p>
                      </div>
                      <div className="p-3 bg-slate-50 dark:bg-[#1c1c27] rounded-xl">
                        <p className="text-xs text-slate-500 dark:text-slate-400 mb-1">토큰 타입</p>
                        <p className="font-medium text-slate-900 dark:text-white text-sm">{modalAccount.tokenType || 'RENEWABLE'}</p>
                      </div>
                      <div className="p-3 bg-slate-50 dark:bg-[#1c1c27] rounded-xl">
                        <p className="text-xs text-slate-500 dark:text-slate-400 mb-1">액세스 토큰</p>
                        <p className="font-medium text-sm">
                          {modalAccount.accessToken
                            ? <span className="text-green-600 dark:text-green-400">설정됨</span>
                            : <span className="text-red-500">미설정</span>
                          }
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Token Dates */}
                  <div className="space-y-3">
                    <h4 className="font-bold text-slate-900 dark:text-white flex items-center gap-2">
                      <span className="material-symbols-outlined text-base">event</span>
                      토큰/활동 이력
                    </h4>
                    <div className="space-y-2">
                      {[
                        { label: '토큰 만료일', value: modalAccount.tokenExpiresAt },
                        { label: '토큰 갱신일', value: modalAccount.tokenRefreshedAt },
                        { label: '토큰 검증일', value: modalAccount.tokenVerifiedAt },
                        { label: '마지막 포스트', value: modalAccount.lastPostDate },
                        { label: '마지막 API 호출', value: modalAccount.lastApiCallAt },
                      ].map(({ label, value }) => (
                        <div key={label} className="flex justify-between items-center py-2 border-b border-slate-100 dark:border-[#282839] last:border-0">
                          <span className="text-sm text-slate-500 dark:text-slate-400">{label}</span>
                          <span className="text-sm font-medium text-slate-900 dark:text-white">
                            {value ? new Date(value).toLocaleString('ko-KR') : '-'}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Post Stats */}
                  <div className="p-4 bg-slate-50 dark:bg-[#1c1c27] rounded-xl">
                    <h4 className="font-bold text-slate-900 dark:text-white mb-3">오늘 포스팅</h4>
                    <div className="flex items-center gap-4">
                      <div className="text-3xl font-black text-primary">{modalAccount.todayPostCount || 0}</div>
                      <div className="text-slate-400">/</div>
                      <div className="text-lg font-bold text-slate-900 dark:text-white">{modalAccount.dailyPostLimit || 25}</div>
                      <span className="text-sm text-slate-500 dark:text-slate-400">건</span>
                    </div>
                  </div>
                </div>
              )}

              {/* === Content Settings Tab === */}
              {modalTab === 'content' && contentSettings && (
                <div className="space-y-6">
                  {/* Category */}
                  <div>
                    <h4 className="font-bold text-slate-900 dark:text-white mb-3 flex items-center gap-2">
                      <span className="material-symbols-outlined text-base">category</span>
                      계정 카테고리
                    </h4>
                    <div className="grid grid-cols-3 gap-2">
                      {CATEGORIES.map(cat => (
                        <button
                          key={cat.value}
                          onClick={() => updateContentField('accountCategory', cat.value)}
                          className={`p-3 rounded-xl border text-sm font-medium flex items-center gap-2 transition-colors ${
                            contentSettings.accountCategory === cat.value
                              ? 'bg-primary/10 border-primary text-primary'
                              : 'bg-slate-50 dark:bg-[#1c1c27] border-slate-200 dark:border-[#282839] text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-[#282839]'
                          }`}
                        >
                          <span className="material-symbols-outlined text-base">{cat.icon}</span>
                          {cat.value}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Sub Category */}
                  <div>
                    <label className="text-sm font-bold text-slate-900 dark:text-white mb-2 block">세부 카테고리</label>
                    <input
                      type="text"
                      value={contentSettings.accountSubCategory || ''}
                      onChange={(e) => updateContentField('accountSubCategory', e.target.value)}
                      placeholder="예: 스킨케어, 메이크업"
                      className={inputClass}
                    />
                  </div>

                  {/* Target Audience */}
                  <div>
                    <h4 className="font-bold text-slate-900 dark:text-white mb-3 flex items-center gap-2">
                      <span className="material-symbols-outlined text-base">groups</span>
                      타겟 고객
                    </h4>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="text-xs font-semibold text-slate-500 dark:text-slate-400 mb-1.5 block">연령대</label>
                        <select
                          value={contentSettings.targetAgeGroup || ''}
                          onChange={(e) => updateContentField('targetAgeGroup', e.target.value)}
                          className={inputClass}
                        >
                          <option value="">선택</option>
                          <option value="10대">10대</option>
                          <option value="2030대">2030대</option>
                          <option value="4050대">4050대</option>
                          <option value="전체">전체</option>
                        </select>
                      </div>
                      <div>
                        <label className="text-xs font-semibold text-slate-500 dark:text-slate-400 mb-1.5 block">성별</label>
                        <select
                          value={contentSettings.targetGender || ''}
                          onChange={(e) => updateContentField('targetGender', e.target.value)}
                          className={inputClass}
                        >
                          <option value="">선택</option>
                          <option value="여성">여성</option>
                          <option value="남성">남성</option>
                          <option value="무관">무관</option>
                        </select>
                      </div>
                    </div>
                    <div className="mt-3">
                      <label className="text-xs font-semibold text-slate-500 dark:text-slate-400 mb-1.5 block">관심사</label>
                      <input
                        type="text"
                        value={contentSettings.targetInterest || ''}
                        onChange={(e) => updateContentField('targetInterest', e.target.value)}
                        placeholder="예: K-POP, 한류, 아이돌"
                        className={inputClass}
                      />
                    </div>
                  </div>

                  {/* Tone & Manner */}
                  <div>
                    <h4 className="font-bold text-slate-900 dark:text-white mb-3 flex items-center gap-2">
                      <span className="material-symbols-outlined text-base">record_voice_over</span>
                      톤 & 매너
                    </h4>
                    <div className="space-y-2">
                      {TONE_OPTIONS.map(tone => (
                        <button
                          key={tone.value}
                          onClick={() => updateContentField('toneAndManner', tone.value)}
                          className={`w-full p-3 rounded-xl border text-left transition-colors ${
                            contentSettings.toneAndManner === tone.value
                              ? 'bg-primary/10 border-primary'
                              : 'bg-slate-50 dark:bg-[#1c1c27] border-slate-200 dark:border-[#282839] hover:bg-slate-100 dark:hover:bg-[#282839]'
                          }`}
                        >
                          <p className={`text-sm font-bold ${contentSettings.toneAndManner === tone.value ? 'text-primary' : 'text-slate-900 dark:text-white'}`}>
                            {tone.label}
                          </p>
                          <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">{tone.desc}</p>
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Visual Guide */}
                  <div>
                    <h4 className="font-bold text-slate-900 dark:text-white mb-3 flex items-center gap-2">
                      <span className="material-symbols-outlined text-base">palette</span>
                      비주얼 가이드
                    </h4>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="text-xs font-semibold text-slate-500 dark:text-slate-400 mb-1.5 block">브랜드 컬러</label>
                        <div className="flex items-center gap-2">
                          <input
                            type="color"
                            value={contentSettings.brandColor || '#1313EC'}
                            onChange={(e) => updateContentField('brandColor', e.target.value)}
                            className="w-11 h-11 rounded-xl border border-slate-200 dark:border-[#3b3b54] cursor-pointer"
                          />
                          <input
                            type="text"
                            value={contentSettings.brandColor || '#1313EC'}
                            onChange={(e) => updateContentField('brandColor', e.target.value)}
                            className={`${inputClass} flex-1 font-mono`}
                          />
                        </div>
                      </div>
                      <div>
                        <label className="text-xs font-semibold text-slate-500 dark:text-slate-400 mb-1.5 block">이미지 무드</label>
                        <select
                          value={contentSettings.imageMood || ''}
                          onChange={(e) => updateContentField('imageMood', e.target.value)}
                          className={inputClass}
                        >
                          <option value="">선택</option>
                          {MOOD_OPTIONS.map(m => (
                            <option key={m} value={m}>{m}</option>
                          ))}
                        </select>
                      </div>
                    </div>
                  </div>

                  {/* Default Caption */}
                  <div>
                    <h4 className="font-bold text-slate-900 dark:text-white mb-3 flex items-center gap-2">
                      <span className="material-symbols-outlined text-base">description</span>
                      게시글 기본 양식
                    </h4>
                    <textarea
                      value={contentSettings.defaultCaption || ''}
                      onChange={(e) => updateContentField('defaultCaption', e.target.value)}
                      placeholder="기본 캡션을 입력하세요..."
                      rows={3}
                      className="w-full px-4 py-3 bg-slate-50 dark:bg-[#1c1c27] border border-slate-200 dark:border-[#3b3b54] rounded-xl focus:ring-2 focus:ring-primary focus:border-transparent outline-none text-slate-900 dark:text-white text-sm resize-none"
                    />
                  </div>

                  {/* Permanent Hashtags */}
                  <div>
                    <label className="text-sm font-bold text-slate-900 dark:text-white mb-2 block">고정 해시태그</label>
                    <div className="flex gap-2 mb-3">
                      <input
                        type="text"
                        value={hashtagInput}
                        onChange={(e) => setHashtagInput(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addHashtag())}
                        placeholder="#해시태그 입력"
                        className={`${inputClass} flex-1`}
                      />
                      <button
                        onClick={addHashtag}
                        className="px-4 h-11 bg-primary text-white rounded-xl font-bold text-sm hover:opacity-90 transition-opacity"
                      >
                        추가
                      </button>
                    </div>
                    {getHashtags().length > 0 && (
                      <div className="flex flex-wrap gap-2">
                        {getHashtags().map(tag => (
                          <span key={tag} className="inline-flex items-center gap-1 px-3 py-1.5 bg-primary/10 text-primary rounded-full text-sm font-medium">
                            #{tag}
                            <button onClick={() => removeHashtag(tag)} className="material-symbols-outlined text-sm hover:opacity-70">close</button>
                          </span>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* 콘텐츠 퀄리티 설정 */}
                  <div>
                    <h4 className="font-bold text-slate-900 dark:text-white mb-3 flex items-center gap-2">
                      <span className="material-symbols-outlined text-base">tune</span>
                      콘텐츠 퀄리티 설정
                    </h4>
                    <div className="space-y-4">
                      {/* 캡션 길이 */}
                      <div>
                        <label className="text-xs font-semibold text-slate-500 dark:text-slate-400 mb-1.5 block">캡션 길이</label>
                        <div className="flex gap-2">
                          {CAPTION_LENGTH_OPTIONS.map(opt => (
                            <button
                              key={opt.value}
                              onClick={() => updateContentField('captionLength', opt.value)}
                              className={`flex-1 px-3 py-2.5 rounded-xl text-sm font-medium transition-all ${
                                (contentSettings.captionLength || 'MEDIUM') === opt.value
                                  ? 'bg-primary text-white shadow-sm'
                                  : 'bg-slate-100 dark:bg-[#1c1c27] text-slate-600 dark:text-slate-400 hover:bg-slate-200 dark:hover:bg-[#252538]'
                              }`}
                              title={opt.desc}
                            >
                              {opt.label}
                            </button>
                          ))}
                        </div>
                        <p className="text-[10px] text-slate-400 mt-1">
                          {CAPTION_LENGTH_OPTIONS.find(o => o.value === (contentSettings.captionLength || 'MEDIUM'))?.desc}
                        </p>
                      </div>

                      {/* CTA 유형 */}
                      <div>
                        <label className="text-xs font-semibold text-slate-500 dark:text-slate-400 mb-1.5 block">CTA (참여 유도)</label>
                        <div className="flex flex-wrap gap-1.5">
                          {CTA_OPTIONS.map(opt => (
                            <button
                              key={opt.value}
                              onClick={() => updateContentField('ctaType', opt.value)}
                              className={`px-3 py-2 rounded-xl text-xs font-medium transition-all flex items-center gap-1 ${
                                (contentSettings.ctaType || 'NONE') === opt.value
                                  ? 'bg-primary text-white shadow-sm'
                                  : 'bg-slate-100 dark:bg-[#1c1c27] text-slate-600 dark:text-slate-400 hover:bg-slate-200 dark:hover:bg-[#252538]'
                              }`}
                            >
                              <span className="material-symbols-outlined" style={{ fontSize: '14px' }}>{opt.icon}</span>
                              {opt.label}
                            </button>
                          ))}
                        </div>
                      </div>

                      {/* 이모지 밀도 */}
                      <div>
                        <label className="text-xs font-semibold text-slate-500 dark:text-slate-400 mb-1.5 block">이모지 사용량</label>
                        <div className="flex gap-2">
                          {EMOJI_DENSITY_OPTIONS.map(opt => (
                            <button
                              key={opt.value}
                              onClick={() => updateContentField('emojiDensity', opt.value)}
                              className={`flex-1 px-3 py-2.5 rounded-xl text-xs font-medium transition-all ${
                                (contentSettings.emojiDensity || 'MEDIUM') === opt.value
                                  ? 'bg-primary text-white shadow-sm'
                                  : 'bg-slate-100 dark:bg-[#1c1c27] text-slate-600 dark:text-slate-400 hover:bg-slate-200 dark:hover:bg-[#252538]'
                              }`}
                              title={opt.desc}
                            >
                              {opt.label}
                            </button>
                          ))}
                        </div>
                      </div>

                      {/* 이미지 스타일 */}
                      <div>
                        <label className="text-xs font-semibold text-slate-500 dark:text-slate-400 mb-1.5 block">AI 이미지 스타일</label>
                        <div className="grid grid-cols-3 gap-1.5">
                          {IMAGE_STYLE_OPTIONS.map(opt => (
                            <button
                              key={opt.value}
                              onClick={() => updateContentField('imageStyle', opt.value)}
                              className={`px-3 py-2.5 rounded-xl text-xs font-medium transition-all flex items-center justify-center gap-1 ${
                                (contentSettings.imageStyle || 'PHOTO') === opt.value
                                  ? 'bg-primary text-white shadow-sm'
                                  : 'bg-slate-100 dark:bg-[#1c1c27] text-slate-600 dark:text-slate-400 hover:bg-slate-200 dark:hover:bg-[#252538]'
                              }`}
                            >
                              <span className="material-symbols-outlined" style={{ fontSize: '14px' }}>{opt.icon}</span>
                              {opt.label}
                            </button>
                          ))}
                        </div>
                      </div>

                      {/* 최소 품질 점수 */}
                      <div>
                        <label className="text-xs font-semibold text-slate-500 dark:text-slate-400 mb-1.5 block">
                          캡션 최소 품질 점수 ({contentSettings.minQualityScore || 0 === 0 ? '비활성' : `${contentSettings.minQualityScore}점 이상`})
                        </label>
                        <input
                          type="range"
                          min="0"
                          max="10"
                          value={contentSettings.minQualityScore || 0}
                          onChange={(e) => updateContentField('minQualityScore', parseInt(e.target.value))}
                          className="w-full h-2 bg-slate-200 dark:bg-[#2a2a3d] rounded-lg appearance-none cursor-pointer accent-primary"
                        />
                        <div className="flex justify-between text-[10px] text-slate-400 mt-1">
                          <span>비활성</span>
                          <span>5</span>
                          <span>10</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Save Button */}
                  {contentDirty && (
                    <button
                      onClick={handleSaveContentSettings}
                      disabled={modalLoading}
                      className="w-full py-3 bg-primary text-white rounded-xl font-bold text-sm hover:opacity-90 transition-opacity disabled:opacity-50 flex items-center justify-center gap-2"
                    >
                      <span className="material-symbols-outlined text-base">save</span>
                      콘텐츠 설정 저장
                    </button>
                  )}
                </div>
              )}
              {modalTab === 'content' && !contentSettings && (
                <div className="flex items-center justify-center py-12">
                  <span className="text-slate-400 dark:text-slate-500">콘텐츠 설정 로딩 중...</span>
                </div>
              )}

              {/* === Schedule Tab (Step 4) === */}
              {modalTab === 'schedule' && modalSchedule && (
                <div className="space-y-5">
                  {/* Auto Upload Toggle */}
                  <div className="flex justify-between items-center p-4 bg-slate-50 dark:bg-[#1c1c27] rounded-xl">
                    <div>
                      <h4 className="font-bold text-slate-900 dark:text-white">자동 업로드</h4>
                      <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                        설정한 시간에 자동으로 업로드합니다
                      </p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={modalSchedule.autoUploadEnabled}
                        onChange={handleToggleAutoUpload}
                        disabled={modalLoading}
                        className="sr-only peer"
                      />
                      <div className="w-14 h-7 bg-slate-200 dark:bg-slate-700 peer-focus:ring-4 peer-focus:ring-primary/30 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-0.5 after:left-[4px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-6 after:w-6 after:transition-all peer-checked:bg-primary"></div>
                    </label>
                  </div>

                  {/* Upload Times */}
                  <div>
                    <h4 className="font-bold text-slate-900 dark:text-white mb-3 flex items-center gap-2">
                      <span className="material-symbols-outlined text-base">schedule</span>
                      업로드 시간
                    </h4>
                    <div className="flex gap-2 mb-3">
                      <input
                        type="time"
                        value={newTime}
                        onChange={(e) => setNewTime(e.target.value)}
                        className={inputClass}
                      />
                      <button
                        onClick={handleAddTime}
                        disabled={modalLoading}
                        className="px-5 h-11 bg-primary text-white rounded-xl font-bold text-sm hover:opacity-90 transition-opacity disabled:opacity-50 flex items-center gap-1"
                      >
                        <span className="material-symbols-outlined text-base">add</span>
                        추가
                      </button>
                    </div>
                    {modalSchedule.uploadTimes?.length > 0 ? (
                      <div className="flex flex-wrap gap-2">
                        {modalSchedule.uploadTimes.map(time => (
                          <div
                            key={time}
                            className="flex items-center gap-1.5 px-3 py-1.5 bg-gradient-to-r from-primary to-purple-600 text-white rounded-full text-sm font-medium"
                          >
                            <span className="material-symbols-outlined text-sm">schedule</span>
                            <span>{time}</span>
                            <button
                              onClick={() => handleRemoveTime(time)}
                              className="material-symbols-outlined text-sm hover:opacity-70 ml-0.5"
                            >
                              close
                            </button>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-sm text-slate-400 dark:text-slate-500 italic">설정된 시간이 없습니다</p>
                    )}
                  </div>

                  {/* Upload Days */}
                  <div>
                    <h4 className="font-bold text-slate-900 dark:text-white mb-3 flex items-center gap-2">
                      <span className="material-symbols-outlined text-base">calendar_month</span>
                      업로드 요일
                    </h4>
                    <div className="flex flex-wrap gap-2">
                      {[
                        { num: 1, label: '월' },
                        { num: 2, label: '화' },
                        { num: 3, label: '수' },
                        { num: 4, label: '목' },
                        { num: 5, label: '금' },
                        { num: 6, label: '토' },
                        { num: 7, label: '일' },
                      ].map(day => {
                        const isSelected = (modalSchedule.daysOfWeek || []).includes(day.num)
                        return (
                          <button
                            key={day.num}
                            onClick={() => handleToggleDay(day.num)}
                            disabled={modalLoading}
                            className={`w-11 h-11 rounded-xl font-bold text-sm transition-all ${
                              isSelected
                                ? 'bg-primary text-white shadow-lg shadow-primary/30'
                                : 'bg-slate-100 dark:bg-[#1c1c27] text-slate-500 dark:text-slate-400 hover:bg-slate-200 dark:hover:bg-slate-700'
                            } disabled:opacity-50`}
                          >
                            {day.label}
                          </button>
                        )
                      })}
                    </div>
                    {(modalSchedule.daysOfWeek || []).length === 0 && (
                      <p className="text-xs text-amber-500 mt-2">요일이 선택되지 않았습니다</p>
                    )}
                  </div>

                  {/* Daily Post Limit */}
                  <div>
                    <h4 className="font-bold text-slate-900 dark:text-white mb-3 flex items-center gap-2">
                      <span className="material-symbols-outlined text-base">bar_chart</span>
                      일일 포스트 제한
                    </h4>
                    <div className="flex items-center gap-3">
                      <input
                        type="number"
                        value={modalSchedule.dailyPostLimit}
                        onChange={(e) => handleUpdateLimit(e.target.value)}
                        min="1"
                        max="100"
                        className="w-24 px-4 h-11 bg-slate-50 dark:bg-[#1c1c27] border border-slate-200 dark:border-[#3b3b54] rounded-xl focus:ring-2 focus:ring-primary focus:border-transparent outline-none text-slate-900 dark:text-white"
                      />
                      <span className="text-sm text-slate-500 dark:text-slate-400">개 / 일 (권장: 25개)</span>
                    </div>
                  </div>
                </div>
              )}
              {modalTab === 'schedule' && !modalSchedule && (
                <div className="flex items-center justify-center py-12">
                  <span className="text-slate-400 dark:text-slate-500">스케줄 정보가 없습니다.</span>
                </div>
              )}

              {/* === Keywords Tab === */}
              {modalTab === 'keywords' && (
                <div className="space-y-4">
                  <p className="text-sm text-slate-500 dark:text-slate-400">
                    이 계정에 설정된 키워드 목록입니다.
                  </p>
                  {modalKeywords.length === 0 ? (
                    <div className="p-8 text-center">
                      <span className="material-symbols-outlined text-4xl text-slate-300 dark:text-slate-700 block mb-2">label</span>
                      <p className="text-slate-400 dark:text-slate-500">설정된 키워드가 없습니다.</p>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {modalKeywords.map(kw => (
                        <div
                          key={kw.id}
                          className="flex items-center justify-between p-3 bg-slate-50 dark:bg-[#1c1c27] rounded-xl border border-slate-200 dark:border-[#282839]"
                        >
                          <div className="flex items-center gap-3">
                            <span className={`material-symbols-outlined text-base ${kw.isActive ? 'text-green-500' : 'text-slate-400'}`}>
                              {kw.isActive ? 'check_circle' : 'cancel'}
                            </span>
                            <div>
                              <span className="font-medium text-slate-900 dark:text-white">{kw.keyword}</span>
                              {kw.hashtags && (
                                <div className="flex flex-wrap gap-1 mt-1">
                                  {(Array.isArray(kw.hashtags) ? kw.hashtags : []).map((tag, i) => (
                                    <span key={i} className="text-xs text-primary">#{tag}</span>
                                  ))}
                                </div>
                              )}
                            </div>
                          </div>
                          <label className="relative inline-flex items-center cursor-pointer">
                            <input
                              type="checkbox"
                              checked={kw.isActive}
                              onChange={() => handleToggleKeyword(kw.id)}
                              disabled={modalLoading}
                              className="sr-only peer"
                            />
                            <div className="w-11 h-6 bg-slate-200 dark:bg-slate-700 peer-focus:ring-4 peer-focus:ring-primary/30 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary"></div>
                          </label>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* === Generation Rule Tab === */}
              {modalTab === 'generation' && (
                <div className="space-y-4">
                  {!generationRule ? (
                    <div className="p-8 text-center">
                      <span className="material-symbols-outlined text-4xl text-slate-300 dark:text-slate-700 block mb-2">auto_awesome</span>
                      <p className="text-slate-400 dark:text-slate-500">생성 규칙이 설정되지 않았습니다.</p>
                    </div>
                  ) : (
                    <>
                      {/* Stats Cards */}
                      <div className="grid grid-cols-3 gap-3">
                        <div className="bg-slate-50 dark:bg-[#1c1c27] rounded-xl p-3 text-center border border-slate-200 dark:border-[#282839]">
                          <p className="text-xs text-slate-500 dark:text-slate-400 mb-1">생성 모드</p>
                          <p className="text-lg font-bold text-slate-900 dark:text-white">
                            {generationRule.generationMode === 'BATCH' ? '일괄' : generationRule.generationMode === 'DAILY' ? '일별' : '자동'}
                          </p>
                        </div>
                        <div className="bg-slate-50 dark:bg-[#1c1c27] rounded-xl p-3 text-center border border-slate-200 dark:border-[#282839]">
                          <p className="text-xs text-slate-500 dark:text-slate-400 mb-1">일일 수량</p>
                          <p className="text-lg font-bold text-slate-900 dark:text-white">{generationRule.dailyCount}</p>
                        </div>
                        <div className="bg-slate-50 dark:bg-[#1c1c27] rounded-xl p-3 text-center border border-slate-200 dark:border-[#282839]">
                          <p className="text-xs text-slate-500 dark:text-slate-400 mb-1">상태</p>
                          <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-bold ${
                            generationRule.status === 'READY' ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' :
                            generationRule.status === 'RUNNING' ? 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400' :
                            generationRule.status === 'COMPLETED' ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400' :
                            'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-400'
                          }`}>
                            {generationRule.status}
                          </span>
                        </div>
                      </div>

                      {/* Progress Stats */}
                      <div className="grid grid-cols-3 gap-3">
                        <div className="bg-slate-50 dark:bg-[#1c1c27] rounded-xl p-3 text-center border border-slate-200 dark:border-[#282839]">
                          <p className="text-xs text-slate-500 dark:text-slate-400 mb-1">총 생성</p>
                          <p className="text-lg font-bold text-slate-900 dark:text-white">{generationRule.totalCount || 0}</p>
                        </div>
                        <div className="bg-slate-50 dark:bg-[#1c1c27] rounded-xl p-3 text-center border border-slate-200 dark:border-[#282839]">
                          <p className="text-xs text-slate-500 dark:text-slate-400 mb-1">성공</p>
                          <p className="text-lg font-bold text-green-600 dark:text-green-400">{generationRule.successCount || 0}</p>
                        </div>
                        <div className="bg-slate-50 dark:bg-[#1c1c27] rounded-xl p-3 text-center border border-slate-200 dark:border-[#282839]">
                          <p className="text-xs text-slate-500 dark:text-slate-400 mb-1">실패</p>
                          <p className="text-lg font-bold text-red-600 dark:text-red-400">{generationRule.failCount || 0}</p>
                        </div>
                      </div>

                      {/* Detail Info */}
                      <div className="bg-slate-50 dark:bg-[#1c1c27] rounded-xl p-4 border border-slate-200 dark:border-[#282839] space-y-2">
                        {generationRule.generationMode !== 'AUTO' && generationRule.startDate && (
                          <div className="flex justify-between text-sm">
                            <span className="text-slate-500 dark:text-slate-400">기간</span>
                            <span className="text-slate-900 dark:text-white">{generationRule.startDate} ~ {generationRule.endDate}</span>
                          </div>
                        )}
                        {generationRule.generationMode === 'AUTO' && (
                          <>
                            <div className="flex justify-between text-sm">
                              <span className="text-slate-500 dark:text-slate-400">미리 생성 일수</span>
                              <span className="text-slate-900 dark:text-white">{generationRule.daysAhead || 3}일</span>
                            </div>
                            <div className="flex justify-between text-sm">
                              <span className="text-slate-500 dark:text-slate-400">자동 생성</span>
                              <span className={generationRule.autoGenerate ? 'text-green-600 dark:text-green-400' : 'text-slate-400'}>
                                {generationRule.autoGenerate ? '활성화' : '비활성화'}
                              </span>
                            </div>
                          </>
                        )}
                        {generationRule.lastExecutedAt && (
                          <div className="flex justify-between text-sm">
                            <span className="text-slate-500 dark:text-slate-400">마지막 실행</span>
                            <span className="text-slate-900 dark:text-white">{generationRule.lastExecutedAt.replace('T', ' ').substring(0, 16)}</span>
                          </div>
                        )}
                      </div>

                      {/* Reset Button */}
                      {(generationRule.status === 'RUNNING' || generationRule.status === 'COMPLETED') && (
                        <button
                          onClick={handleResetGenerationRule}
                          disabled={generationRuleLoading}
                          className="w-full py-2.5 bg-orange-500 hover:bg-orange-600 text-white rounded-xl font-bold text-sm transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                        >
                          <span className="material-symbols-outlined text-sm">restart_alt</span>
                          {generationRuleLoading ? '초기화 중...' : '상태 초기화 (READY로 변경)'}
                        </button>
                      )}
                    </>
                  )}
                </div>
              )}

              {/* === Calendar Tab === */}
              {modalTab === 'calendar' && modalAccount && (
                <ContentCalendarPage
                  accountId={modalAccount.id}
                  embedded={true}
                  apiOverride={adminCalendarAPI}
                  readOnly={true}
                />
              )}
            </div>

            {/* Modal Footer */}
            <div className="border-t border-slate-200 dark:border-[#282839] p-5">
              <button
                onClick={closeModal}
                className="w-full py-3 bg-primary text-white rounded-xl font-bold text-sm hover:opacity-90 transition-opacity"
              >
                닫기
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default MemberManagementPage
